import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "@tanstack/react-router";
import {
  changeSentryInvestigationsMutationOptions,
  sentryInvestigationsQueryOptions,
} from "../../lib/operatorsAdminReactQuery";
import { SettingsSection } from "./SettingsChrome";

export function SentryInvestigationsSection() {
  const [before, setBefore] = useState<string>();
  const queryClient = useQueryClient();
  const query = useQuery(sentryInvestigationsQueryOptions(before ? { before } : undefined));
  const change = useMutation(changeSentryInvestigationsMutationOptions({ queryClient }));
  const data = query.data;
  const canEdit = Boolean(data?.configuration.enabled) && !query.isError && !change.isPending;
  const canPause =
    Boolean(data?.state.activatedAt) && !data?.state.paused && !query.isError && !change.isPending;
  const button =
    "rounded-lg border border-border px-3 py-1.5 text-sm hover:bg-muted/40 disabled:opacity-50";
  return (
    <SettingsSection
      title="Sentry investigations"
      description="Production errors are investigated automatically. Results appear in GitHub and Linear. This workflow sends no notifications."
    >
      {query.isLoading ? (
        <p className="text-sm text-muted-foreground">Loading investigations…</p>
      ) : null}
      {query.isError ? (
        <p role="alert" className="text-sm text-destructive">
          {query.error.message}
        </p>
      ) : null}
      {data ? (
        <>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="text-sm">
              <p>
                {!data.configuration.enabled
                  ? "Setup incomplete"
                  : !data.state.activatedAt
                    ? "Not started"
                    : data.state.paused
                      ? "Paused"
                      : "Active"}
              </p>
              <p className="text-muted-foreground">
                Up to {data.configuration.maxConcurrent} concurrent investigations and{" "}
                {data.configuration.maxDailyStarts} daily starts, New York time.
              </p>
              <p className="text-muted-foreground">
                Last successful poll:{" "}
                {data.state.lastPollAt
                  ? new Date(data.state.lastPollAt).toLocaleString()
                  : "Not yet completed"}
              </p>
            </div>
            <button
              className={button}
              disabled={!canEdit && !canPause}
              onClick={() => change.mutate({ paused: !data.state.paused })}
            >
              {!data.state.activatedAt
                ? "Start investigations"
                : data.state.paused
                  ? "Resume investigations"
                  : "Pause investigations"}
            </button>
          </div>
          {!data.configuration.enabled && data.configuration.missingRequirements?.length ? (
            <p className="mt-3 text-sm text-muted-foreground">
              Missing setup: {data.configuration.missingRequirements.join(", ")}.
            </p>
          ) : null}
          <p className="mt-3 text-sm text-muted-foreground">
            Unresolved production issues, including the backlog and old issues with new events, are
            investigated once. Further investigations require a regression or useful new evidence.
            Backlog discovery searches up to 90 days of retained production events. Sentry may
            return less history depending on retention; dormant issues outside that window are
            discovered when they recur.
          </p>
          {data.state.activatedAt ? (
            <p className="mt-2 text-sm text-muted-foreground">
              {data.state.backlogCompletedAt
                ? "Backlog scan complete. Queued investigations start within the daily limit."
                : "Backlog scan pending. Each successful poll queues another page of issues."}
            </p>
          ) : null}
          {data.state.lastError ? (
            <p role="alert" className="mt-3 text-sm text-destructive">
              {data.state.lastError}
            </p>
          ) : null}
          <ul className="mt-4 divide-y divide-border">
            {data.items.map((run) => (
              <li key={run.id} className="flex flex-wrap items-start justify-between gap-3 py-3">
                <div className="min-w-0 flex-1 text-sm">
                  <a
                    href={`https://sentry.io/organizations/craniometrix-ec/issues/${run.issueId}/`}
                    target="_blank"
                    rel="noreferrer"
                    className="font-medium underline"
                  >
                    {run.snapshot.shortId}
                  </a>
                  <span className="ml-2 text-muted-foreground">
                    {run.snapshot.project} · {run.status.replaceAll("_", " ")}
                  </span>
                  <p className="mt-1 break-words">{run.snapshot.title}</p>
                  {run.outcome ? (
                    <p className="mt-1 text-muted-foreground">{run.outcome.summary}</p>
                  ) : null}
                  {run.error ? <p className="mt-1 text-destructive">{run.error}</p> : null}
                  <div className="mt-2 flex flex-wrap gap-3">
                    {run.threadId ? (
                      <Link
                        to="/operators/$threadId"
                        params={{ threadId: run.threadId }}
                        className="underline"
                      >
                        Investigation
                      </Link>
                    ) : null}
                    {run.outcome?.url ? (
                      <a
                        href={run.outcome.url}
                        target="_blank"
                        rel="noreferrer"
                        className="underline"
                      >
                        {run.outcome.url.startsWith("https://linear.app/")
                          ? "Linear issue"
                          : "Pull request"}
                      </a>
                    ) : null}
                    {run.outcome?.relatedRunId ? (
                      <Link
                        to="/operators/$threadId"
                        params={{ threadId: run.outcome.relatedRunId }}
                        className="underline"
                      >
                        Owning investigation
                      </Link>
                    ) : null}
                  </div>
                </div>
                {run.status === "failed" ? (
                  <button
                    className={button}
                    disabled={!canEdit}
                    onClick={() => change.mutate({ retryId: run.id })}
                  >
                    Retry investigation
                  </button>
                ) : null}
              </li>
            ))}
          </ul>
          {!data.items.length ? (
            <p className="mt-4 text-sm text-muted-foreground">No investigations on this page.</p>
          ) : null}
          <div className="mt-3 flex gap-2">
            {before ? (
              <button className={button} onClick={() => setBefore(undefined)}>
                Latest investigations
              </button>
            ) : null}
            {data.nextBefore ? (
              <button className={button} onClick={() => setBefore(data.nextBefore!)}>
                Older investigations
              </button>
            ) : null}
          </div>
        </>
      ) : null}
      {change.isError ? (
        <p role="alert" className="mt-3 text-sm text-destructive">
          {change.error.message}
        </p>
      ) : null}
    </SettingsSection>
  );
}
